import React, { useRef } from 'react';
import { Avatar, Box, Button, Card, CardContent, Chip, Divider, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import moment from 'moment';
import { getRole } from 'Service/Service';
import { ROLE_COLORS, ROLE_LABELS } from 'themes/colors';

interface AccountProfileProps {
  user: {
    name: string;
    city: string;
    profile_url: string;
    last_login_timestamp: string;
  };
  photoPreview?: string;
  onPhotoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhotoUpload: (e: React.MouseEvent) => void;
  hasNewPhoto: boolean;
}

export const AccountProfile = ({
  user,
  photoPreview,
  onPhotoChange,
  onPhotoUpload,
  hasNewPhoto,
}: AccountProfileProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const role = getRole();
  const accentColor = ROLE_COLORS[role] || ROLE_COLORS.admin;
  const avatarSrc = photoPreview || user.profile_url;

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          {/* Avatar with colored ring */}
          <Box
            sx={{
              position: 'relative',
              p: '3px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${accentColor}, ${alpha(accentColor, 0.35)})`,
            }}
          >
            <Avatar
              src={avatarSrc}
              sx={{
                width: 96,
                height: 96,
                fontSize: 36,
                fontWeight: 700,
                bgcolor: alpha(accentColor, 0.12),
                color: accentColor,
                border: '3px solid white',
              }}
            >
              {user.name?.[0]?.toUpperCase()}
            </Avatar>

            {/* Camera icon overlay */}
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: accentColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '2px solid white',
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.85 },
              }}
            >
              <CameraAltOutlinedIcon sx={{ fontSize: 14, color: 'white' }} />
            </Box>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={onPhotoChange}
          />

          {/* Name */}
          <Typography variant="h6" fontWeight={700} textAlign="center">
            {user.name || 'User'}
          </Typography>

          {/* Role badge */}
          <Chip
            label={ROLE_LABELS[role] || role}
            size="small"
            sx={{
              bgcolor: alpha(accentColor, 0.1),
              color: accentColor,
              fontWeight: 700,
              fontSize: 11,
              height: 22,
            }}
          />

          {/* City */}
          {user.city && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {user.city}
              </Typography>
            </Box>
          )}

          {/* Last login */}
          {user.last_login_timestamp && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled">
                {moment(user.last_login_timestamp).format('lll')}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      {hasNewPhoto && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              onClick={onPhotoUpload}
              sx={{ borderRadius: 2 }}
            >
              Save Photo
            </Button>
          </Box>
        </>
      )}
    </Card>
  );
};
